import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Camera, Save, Database, Download, Trash2, RefreshCw,
  CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp, X, Image,
  Settings, Smartphone, AlertTriangle, Eye, Plus, ArrowRight, ArrowLeft,
  Users, Edit3, Activity, Wifi, WifiOff, Terminal, Search, Play
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/* ============================================================
   TYPES
   ============================================================ */

interface StudentImage {
  id: string;
  file: File | null;
  preview: string;
  status: 'pending' | 'ocr' | 'done' | 'error';
  ocrText?: string;
  error?: string;
}

interface StudentEntry {
  id: string;           // local uid
  dbId?: number;        // DB id if saved
  images: StudentImage[];
  extracted: Record<string, any>;
  status: 'images' | 'processing' | 'done' | 'error';
  error?: string;
  logs: string[];
  dbStatus: 'new' | 'pending' | 'active'; // DB state
  dirty: boolean;       // edited after last save/load
}

/* ============================================================
   CONSTANTS
   ============================================================ */

const OCR_SPACES = [
  'https://dbuidc-universal-ocr-1.hf.space',
  'https://dbuidc-universal-ocr-2.hf.space',
  'https://dbuidc-universal-ocr-3.hf.space',
  'https://husxeld-universal-ocr-4.hf.space',
  'https://husxeld-universal-ocr-5.hf.space',
  'https://husxeld-universal-ocr-6.hf.space',
];

const AI_ENDPOINTS = [
  'https://opencode1.3mdigitech.qzz.io/v1/chat/completions',
  'https://opencode2.3mdigitech.qzz.io/v1/chat/completions',
  'https://opencode3.3mdigitech.qzz.io/v1/chat/completions',
  'https://opencode4.3mdigitech.qzz.io/v1/chat/completions',
  'https://opencode5.3mdigitech.qzz.io/v1/chat/completions',
  'https://freebuff.3mdigitech.qzz.io/v1/chat/completions',
];

const AI_PROMPT = `Extract student admission data from OCR text. Return ONLY valid JSON, no markdown.
Fields (null if not found): studentName, gender (male/female/other), dob (YYYY-MM-DD), admissionDate (YYYY-MM-DD), rollNo, registrationNo, schoolName, schoolBoard, previousPercentage, fatherName, fatherOccupation, motherName, motherOccupation, fatherPhone1 (10 digits), motherPhone1 (10 digits), whatsappNumber (10 digits), houseNo, villageTown, postOffice, district, fullAddress.`;

const ALL_FIELDS: [string, string, string][] = [
  ['studentName', 'Student Name *', 'text'],
  ['gender', 'Gender', 'select:male,female,other'],
  ['dob', 'Date of Birth', 'date'],
  ['admissionDate', 'Admission Date', 'date'],
  ['rollNo', 'Roll No', 'text'],
  ['registrationNo', 'Reg. No', 'text'],
  ['schoolName', 'Previous School', 'text'],
  ['schoolBoard', 'Board', 'text'],
  ['previousPercentage', 'Prev %', 'number'],
  ['status', 'Status', 'select:active,drop,left,enquiry,pending'],
  ['fatherName', 'Father Name', 'text'],
  ['fatherOccupation', 'Father Occupation', 'text'],
  ['motherName', 'Mother Name', 'text'],
  ['motherOccupation', 'Mother Occupation', 'text'],
  ['fatherPhone1', 'Father Phone', 'tel'],
  ['motherPhone1', 'Mother Phone', 'tel'],
  ['whatsappNumber', 'WhatsApp', 'tel'],
  ['houseNo', 'House No', 'text'],
  ['villageTown', 'Village/Town', 'text'],
  ['postOffice', 'Post Office', 'text'],
  ['district', 'District', 'text'],
  ['fullAddress', 'Full Address', 'textarea'],
];

/* ============================================================
   UTILITIES
   ============================================================ */

function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function sortByRoll(students: StudentEntry[]): StudentEntry[] {
  return [...students].sort((a, b) => {
    const ra = parseInt(a.extracted?.rollNo || '999999', 10);
    const rb = parseInt(b.extracted?.rollNo || '999999', 10);
    if (isNaN(ra) && isNaN(rb)) return 0;
    if (isNaN(ra)) return 1;
    if (isNaN(rb)) return -1;
    return ra - rb;
  });
}

function dbStudentToEntry(s: any): StudentEntry {
  return {
    id: `db-${s.id}`,
    dbId: s.id,
    images: [],
    extracted: {
      studentName: s.student_name,
      gender: s.gender,
      dob: s.date_of_birth,
      admissionDate: s.admission_date,
      rollNo: s.roll_no,
      registrationNo: s.registration_no,
      schoolName: s.school_name,
      schoolBoard: s.school_board,
      previousPercentage: s.previous_percentage,
      status: s.status,
      fatherName: s.father_name,
      fatherOccupation: s.father_occupation,
      motherName: s.mother_name,
      motherOccupation: s.mother_occupation,
      fatherPhone1: s.father_phone_1,
      motherPhone1: s.mother_phone_1,
      whatsappNumber: s.whatsapp_number,
      houseNo: s.house_no,
      villageTown: s.village_town,
      postOffice: s.post_office,
      district: s.district,
      fullAddress: s.full_address,
    },
    status: 'done',
    logs: [],
    dbStatus: s.status === 'pending' ? 'pending' : 'active',
    dirty: false,
  };
}

/* OCR + AI functions */
async function raceOCR(blob: Blob, onLog: (msg: string) => void): Promise<string> {
  const controller = new AbortController();
  const form = new FormData();
  form.append('file', blob, 'photo.jpg');

  onLog('📤 Uploading to 6 OCR spaces...');

  const uploads = OCR_SPACES.map(async (space, i) => {
    try {
      const res = await fetch(`${space}/upload`, { method: 'POST', body: form, signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as any;
      return { space: i + 1, jobId: data.job_id, url: space };
    } catch (err: any) {
      if (err.name === 'AbortError') throw err;
      return null;
    }
  });

  const results = await Promise.allSettled(uploads);
  const jobs: any[] = [];
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value?.jobId) jobs.push(r.value);
  }
  if (jobs.length === 0) throw new Error('All OCR uploads failed');
  onLog(`✅ Uploaded to ${jobs.length}/6 spaces`);

  const polls = jobs.map(async (job: any) => {
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 1500));
      try {
        const res = await fetch(`${job.url}/status/${job.jobId}`, { signal: controller.signal });
        if (!res.ok) continue;
        const data = await res.json() as any;
        if (data.status === 'completed') {
          const pages = data.result?.pages || [];
          const text = pages.map((p: any) => p.content).filter(Boolean).join('\n');
          if (text.length > 20) {
            onLog(`✅ OCR-${job.space} done (${text.length} chars)`);
            try { controller.abort(); } catch {}
            return text;
          }
        }
        if (data.status === 'failed') return null;
      } catch (err: any) {
        if (err.name === 'AbortError') throw err;
      }
    }
    return null;
  });

  const pollResults = await Promise.allSettled(polls);
  for (const r of pollResults) {
    if (r.status === 'fulfilled' && r.value && typeof r.value === 'string' && r.value.length > 20) return r.value;
  }
  throw new Error('All OCR polls failed');
}

async function raceAI(ocrText: string, onLog: (msg: string) => void): Promise<Record<string, any>> {
  const controller = new AbortController();
  onLog('🤖 Sending to AI...');

  const requests = AI_ENDPOINTS.map(async (url, i) => {
    try {
      const model = url.includes('freebuff') ? 'minimax/minimax-m3' : 'opencode/deepseek-v4-flash-free';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: AI_PROMPT },
            { role: 'user', content: ocrText.slice(0, 6000) }
          ],
          temperature: 0.1,
          max_tokens: 1200,
          stream: false,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as any;
      const content = data?.choices?.[0]?.message?.content || '';
      const match = content.match(/\{[\s\S]*\}/);
      if (!match) throw new Error('No JSON');
      const parsed = JSON.parse(match[0]);
      if (parsed.gender) {
        const g = String(parsed.gender).toLowerCase();
        parsed.gender = g === 'm' || g === 'male' ? 'male' : g === 'f' || g === 'female' ? 'female' : null;
      }
      onLog(`✅ AI-${i + 1} done`);
      try { controller.abort(); } catch {}
      return parsed;
    } catch (err: any) {
      if (err.name === 'AbortError') throw err;
      return null;
    }
  });

  const results = await Promise.allSettled(requests);
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value && typeof r.value === 'object') return r.value;
  }
  throw new Error('All AI endpoints failed');
}

/* ============================================================
   STATUS PANEL
   ============================================================ */

function StatusPanel() {
  const [ocr, setOcr] = useState<Record<string, string>>({});
  const [ai, setAi] = useState<Record<string, string>>({});

  const check = async () => {
    const o: Record<string, string> = {};
    OCR_SPACES.forEach(u => o[u] = 'checking');
    setOcr(o);
    const a: Record<string, string> = {};
    AI_ENDPOINTS.forEach(u => a[u] = 'checking');
    setAi(a);

    OCR_SPACES.forEach(async (url) => {
      try {
        const res = await fetch(url + '/', { signal: AbortSignal.timeout(8000) });
        const data = await res.json();
        setOcr(prev => ({ ...prev, [url]: data.status === 'ok' ? 'online' : 'offline' }));
      } catch { setOcr(prev => ({ ...prev, [url]: 'offline' })); }
    });

    AI_ENDPOINTS.forEach(async (url) => {
      try {
        const res = await fetch(url.replace('/v1/chat/completions', '/healthz'), { signal: AbortSignal.timeout(8000) });
        setAi(prev => ({ ...prev, [url]: res.ok ? 'online' : 'offline' }));
      } catch { setAi(prev => ({ ...prev, [url]: 'offline' })); }
    });
  };

  useEffect(() => { check(); }, []);

  const ocrUp = Object.values(ocr).filter(s => s === 'online').length;
  const aiUp = Object.values(ai).filter(s => s === 'online').length;

  return (
    <div className="glass-card p-3 rounded-xl space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-purple-500" />
          <span className="text-xs font-medium text-light-text dark:text-dark-text">Services</span>
        </div>
        <button onClick={check} className="h-6 px-2 rounded border text-[10px] hover:bg-light-elevated text-light-text dark:text-dark-text">
          <RefreshCw className="w-2.5 h-2.5 inline mr-1" />Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex items-center gap-2 px-2 py-1 rounded bg-light-elevated dark:bg-dark-elevated">
          {ocrUp > 0 ? <Wifi className="w-3 h-3 text-lime-500" /> : <WifiOff className="w-3 h-3 text-rose-500" />}
          <span className="text-[10px] text-light-text dark:text-dark-text">OCR: {ocrUp}/{OCR_SPACES.length}</span>
        </div>
        <div className="flex items-center gap-2 px-2 py-1 rounded bg-light-elevated dark:bg-dark-elevated">
          {aiUp > 0 ? <Wifi className="w-3 h-3 text-lime-500" /> : <WifiOff className="w-3 h-3 text-rose-500" />}
          <span className="text-[10px] text-light-text dark:text-dark-text">AI: {aiUp}/{AI_ENDPOINTS.length}</span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function BulkImportPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<'setup' | 'add' | 'review'>('setup');
  const [instId, setInstId] = useState('');
  const [classId, setClassId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [students, setStudents] = useState<StudentEntry[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [activeCell, setActiveCell] = useState<{ sid: string; field: string } | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [continuousCamera, setContinuousCamera] = useState(false);
  const [previewImg, setPreviewImg] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ sid: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [loading, setLoading] = useState(false);

  const procRef = useRef<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<number | null>(null);

  // Auto-scroll on hover near edges
  const handleScrollMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const container = scrollRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const edgeSize = 80;
    const scrollSpeed = 15;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (x < edgeSize) {
      scrollIntervalRef.current = window.setInterval(() => { container.scrollLeft -= scrollSpeed; }, 30);
    } else if (x > rect.width - edgeSize) {
      scrollIntervalRef.current = window.setInterval(() => { container.scrollLeft += scrollSpeed; }, 30);
    }
  }, []);

  const handleScrollMouseLeave = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Data
  const { data: instData } = useQuery('institutes-list', () => api.get('/api/institutes'));
  const institutes = instData?.data || [];
  const { data: clsData } = useQuery(['classes-list', instId], () => api.get(`/api/classes?instituteId=${instId}`), { enabled: !!instId });
  const classes = clsData?.data || [];
  const { data: batData } = useQuery(['batches', classId, instId], () => api.get(`/api/batches?classId=${classId}&instituteId=${instId}`), { enabled: !!classId });
  const batches = batData?.data || [];

  function useQuery(key: any, fn: any, opts?: any) {
    const [data, setData] = useState<any>(null);
    useEffect(() => {
      if (opts?.enabled === false) return;
      fn().then(setData).catch(() => {});
    }, [Array.isArray(key) ? key.join(',') : key]);
    return { data };
  }

  /* ---------- Load existing students ---------- */
  const loadExisting = useCallback(async () => {
    if (!instId || !classId) return;
    setLoading(true);
    try {
      let url = `/api/students?instituteId=${instId}&classId=${classId}&pageSize=500`;
      if (batchId && batchId !== 'total') url += `&batchId=${batchId}`;
      const res: any = await api.get(url);
      const list: any[] = res?.data || [];

      const dbEntries = list.map(dbStudentToEntry);

      setStudents(prev => {
        // Keep only new OCR students not yet in DB
        const dbIds = new Set(dbEntries.map(s => s.dbId));
        const newOcr = prev.filter(s => s.dbStatus === 'new' && !dbIds.has(s.dbId));
        return sortByRoll([...dbEntries, ...newOcr]);
      });

      const active = list.filter(s => s.status === 'active').length;
      const pending = list.filter(s => s.status === 'pending').length;
      toast.success(`Loaded ${active} active + ${pending} pending`);
    } catch (err: any) {
      toast.error(`Load failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [instId, classId, batchId]);

  /* ---------- Log helper ---------- */
  const addLog = useCallback((sid: string, msg: string) => {
    const t = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setStudents(prev => prev.map(s => s.id === sid ? { ...s, logs: [...s.logs, `[${t}] ${msg}`] } : s));
  }, []);

  /* ---------- Process student images ---------- */
  const processStudent = useCallback(async (sid: string) => {
    if (procRef.current.has(sid)) return;
    procRef.current.add(sid);

    setStudents(prev => prev.map(s => s.id === sid ? { ...s, status: 'processing', logs: [] } : s));
    addLog(sid, '🚀 Processing...');

    try {
      const student = students.find(s => s.id === sid);
      if (!student || student.images.length === 0) throw new Error('No images');

      // OCR all images
      setStudents(prev => prev.map(s => s.id === sid ? { ...s, images: s.images.map(i => ({ ...i, status: 'ocr' as const })) } : s));

      const ocrTexts: string[] = [];
      for (let i = 0; i < student.images.length; i++) {
        const img = student.images[i];
        if (!img.file) continue;
        try {
          const text = await raceOCR(img.file, (m) => addLog(sid, `  [Img${i + 1}] ${m}`));
          ocrTexts.push(text);
        } catch (err: any) {
          addLog(sid, `  [Img${i + 1}] ❌ ${err.message}`);
        }
      }

      if (ocrTexts.length === 0) throw new Error('All OCR failed');

      // Free image memory
      student.images.forEach(img => { if (img.preview) URL.revokeObjectURL(img.preview); });
      setStudents(prev => prev.map(s => s.id === sid ? {
        ...s,
        images: s.images.map(i => ({ ...i, file: null, preview: '', status: 'done' as const }))
      } : s));

      addLog(sid, `✅ OCR done: ${ocrTexts.length} texts`);

      // AI extraction
      const combined = ocrTexts.join('\n\n---\n\n');
      const extracted = await raceAI(combined, (m) => addLog(sid, `  ${m}`));
      extracted.status = extracted.status || 'active';

      addLog(sid, `✅ Done: ${Object.keys(extracted).filter(k => extracted[k] != null).length} fields`);

      setStudents(prev => sortByRoll(prev.map(s => s.id === sid ? { ...s, extracted, status: 'done' } : s)));
    } catch (err: any) {
      addLog(sid, `❌ ${err.message}`);
      setStudents(prev => prev.map(s => s.id === sid ? { ...s, status: 'error', error: err.message, images: s.images.map(i => ({ ...i, status: (i.status === 'ocr' ? 'error' : i.status) as StudentImage['status'] })) } : s));
    } finally {
      procRef.current.delete(sid);
    }
  }, [students, addLog]);

  /* ---------- Add images ---------- */
  const addImages = useCallback((files: FileList | File[]) => {
    const newImgs: StudentImage[] = Array.from(files).map(f => ({
      id: uid(), file: f, preview: URL.createObjectURL(f), status: 'pending',
    }));
    setStudents(prev => {
      const last = prev[prev.length - 1];
      if (last && last.status === 'images') {
        return prev.map(s => s.id === last.id ? { ...s, images: [...s.images, ...newImgs] } : s);
      }
      return [...prev, { id: uid(), images: newImgs, extracted: {}, status: 'images', logs: [], dbStatus: 'new', dirty: false }];
    });
  }, []);

  /* ---------- Add new student ---------- */
  const addNewStudent = useCallback(() => {
    const last = students[students.length - 1];
    if (last && last.status === 'images' && last.images.length > 0) processStudent(last.id);
    setStudents(prev => [...prev, { id: uid(), images: [], extracted: {}, status: 'images', logs: [], dbStatus: 'new', dirty: false }]);
  }, [students, processStudent]);

  /* ---------- Update field ---------- */
  const updateField = useCallback((sid: string, field: string, value: any) => {
    setStudents(prev => prev.map(s => s.id === sid ? { ...s, extracted: { ...s.extracted, [field]: value }, dirty: true } : s));
  }, []);

  /* ---------- Remove student ---------- */
  const doRemove = useCallback(async (sid: string) => {
    const s = students.find(x => x.id === sid);
    if (s?.dbId) {
      try {
        await api.delete(`/api/students/${s.dbId}?instituteId=${instId}`);
        toast.success('Student deleted from database');
      } catch (err: any) {
        toast.error(`Delete failed: ${err.message}`);
        return;
      }
    }
    setStudents(prev => {
      const st = prev.find(x => x.id === sid);
      if (st) st.images.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); });
      return prev.filter(x => x.id !== sid);
    });
    setDeleteConfirm(null);
    if (!s?.dbId) toast.success('Removed');
  }, [students]);

  /* ---------- Save/Update ---------- */
  const handleSave = useCallback(async () => {
    // New students → INSERT as pending
    const newOnes = students.filter(s => s.dbStatus === 'new' && s.status === 'done');
    // Dirty existing students → UPDATE
    const dirtyOnes = students.filter(s => s.dbStatus !== 'new' && s.dirty && s.dbId);

    if (newOnes.length === 0 && dirtyOnes.length === 0) {
      toast.error('Nothing to save');
      return;
    }

    setSaving(true);
    try {
      let totalSaved = 0;

      // INSERT new students
      if (newOnes.length > 0) {
        const data = newOnes.map(s => ({
          ...s.extracted,
          classId: Number(classId),
          batchId: batchId === 'total' ? Number(s.extracted.batchId || 0) || null : Number(batchId),
          instituteId: Number(instId),
          status: 'pending',
        }));
        const res: any = await api.post('/api/students/bulk', { students: data, mode: 'insert' });
        const inserted = res?.data?.inserted || 0;
        const errors = res?.data?.errors || [];
        totalSaved += inserted;
        if (errors.length > 0) {
          toast.error(`Inserted ${inserted}, ${errors.length} errors: ${errors[0].message}`);
        }
      }

      // UPDATE dirty existing students — use PATCH individually (bulk update by roll_no fails on Turso)
      if (dirtyOnes.length > 0) {
        let updated = 0;
        for (const s of dirtyOnes) {
          try {
            await api.patch(`/api/students/${s.dbId}?instituteId=${instId}`, {
              studentName: s.extracted.studentName,
              gender: s.extracted.gender,
              dateOfBirth: s.extracted.dob,
              batchId: batchId === 'total' ? Number(s.extracted.batchId || 0) || null : Number(batchId),
              admissionDate: s.extracted.admissionDate,
              status: s.extracted.status,
              schoolName: s.extracted.schoolName,
              schoolBoard: s.extracted.schoolBoard,
              rollNo: s.extracted.rollNo,
            });
            updated++;
          } catch (e: any) {
            toast.error(`Failed to update ${s.extracted.studentName}: ${e.message}`);
          }
        }
        totalSaved += updated;
      }

      // Clear new entries and reset dirty flags, then reload from DB
      setStudents(prev => {
        const remaining = prev
          .filter(s => s.dbStatus !== 'new')  // Remove all new entries (they're now in DB)
          .map(s => ({ ...s, dirty: false })); // Reset dirty flags
        return remaining;
      });

      await loadExisting();

      if (totalSaved > 0) {
        toast.success(`Saved ${totalSaved} students`);
      }
    } catch (err: any) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [students, classId, batchId, instId, loadExisting]);

  /* ---------- Push to DB ---------- */
  const handlePush = useCallback(async () => {
    const pending = students.filter(s => s.dbStatus === 'pending' && s.dbId);
    if (pending.length === 0) {
      toast.error('No pending students to push');
      return;
    }

    setPushing(true);
    try {
      const data = pending.map(s => ({
        ...s.extracted,
        classId: Number(classId),
        batchId: batchId === 'total' ? Number(s.extracted.batchId || 0) || null : Number(batchId),
        instituteId: Number(instId),
        status: 'active',
        rollNo: s.extracted.rollNo,
      }));
      const res: any = await api.post('/api/students/bulk', { students: data, mode: 'update' });
      const updated = res?.data?.updated || 0;
      toast.success(`Pushed ${updated} students to active`);
      await loadExisting();
    } catch (err: any) {
      toast.error(`Push failed: ${err.message}`);
    } finally {
      setPushing(false);
    }
  }, [students, classId, batchId, instId, loadExisting]);

  /* ---------- Export CSV ---------- */
  const exportCSV = useCallback(() => {
    const data = students.filter(s => s.status === 'done').map(s => s.extracted);
    if (!data.length) { toast.error('No data'); return; }
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(s => Object.values(s).map(v => {
      const str = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
      return `"${str.replace(/"/g, '""')}"`;
    }).join(','));
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href: url, download: `students-${Date.now()}.csv` }).click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  }, [students]);

  /* ---------- Stats ---------- */
  const newCount = students.filter(s => s.dbStatus === 'new' && s.status === 'done').length;
  const pendingCount = students.filter(s => s.dbStatus === 'pending').length;
  const dirtyCount = students.filter(s => s.dirty && s.dbStatus !== 'new').length;
  const processingCount = students.filter(s => s.status === 'processing').length;
  const errorCount = students.filter(s => s.status === 'error').length;

  const filtered = search
    ? students.filter(s => {
        const q = search.toLowerCase();
        const d = s.extracted;
        return (d.studentName || '').toLowerCase().includes(q) || (d.rollNo || '').includes(q) || (d.fatherPhone1 || '').includes(q);
      })
    : students;

  /* Cleanup */
  useEffect(() => {
    return () => { students.forEach(s => s.images.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); })); };
  }, []);

  /* ============================================================ */
  return (
    <div className="space-y-4 pb-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <Upload className="w-6 h-6 text-lime-600" /> Bulk Import
          </h1>
          <p className="text-xs text-light-text-muted mt-0.5">OCR + AI powered mass student admission</p>
        </div>
        <div className="flex items-center gap-2">
          {step !== 'setup' && (
            <div className="flex items-center gap-2 text-xs">
              <Users className="w-3.5 h-3.5 text-light-text-muted" />
              <span className="font-medium text-light-text dark:text-dark-text">{students.length} total</span>
              {newCount > 0 && <span className="text-lime-500">({newCount} new)</span>}
              {pendingCount > 0 && <span className="text-amber-500">({pendingCount} pending)</span>}
              {dirtyCount > 0 && <span className="text-purple-500">({dirtyCount} edited)</span>}
              {processingCount > 0 && <span className="text-blue-500">({processingCount} processing)</span>}
              {errorCount > 0 && <span className="text-rose-500">({errorCount} failed)</span>}
            </div>
          )}
          <button onClick={() => setShowStatus(!showStatus)} className="h-8 px-2 rounded-lg border text-xs hover:bg-light-elevated text-light-text dark:text-dark-text"><Activity className="w-3.5 h-3.5" /></button>
          <button onClick={() => setShowSettings(!showSettings)} className="h-8 px-2 rounded-lg border text-xs hover:bg-light-elevated text-light-text dark:text-dark-text"><Settings className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {/* Collapsible panels */}
      <AnimatePresence>
        {showStatus && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden"><StatusPanel /></motion.div>}
      </AnimatePresence>
      <AnimatePresence>
        {showSettings && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="glass-card p-3 rounded-xl flex items-center gap-4">
              <Smartphone className="w-3.5 h-3.5 text-lime-500" />
              <label className="text-xs text-light-text-muted">Continuous Camera:</label>
              <button onClick={() => setContinuousCamera(!continuousCamera)} className={`w-9 h-5 rounded-full transition-colors ${continuousCamera ? 'bg-lime-500' : 'bg-gray-300 dark:bg-gray-600'} relative`}>
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${continuousCamera ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
              <span className="text-[10px] text-light-text-muted">OCR: 6 spaces (race) · AI: 6 endpoints (race)</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ==================== SETUP ==================== */}
      {step === 'setup' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 rounded-2xl space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Select Target</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select value={instId} onChange={e => setInstId(e.target.value)} className="h-11 text-sm rounded-xl border bg-white dark:bg-dark-elevated px-3 outline-none text-light-text dark:text-dark-text">
              <option value="">Select Institute *</option>
              {institutes.map((i: any) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <select value={classId} onChange={e => setClassId(e.target.value)} className="h-11 text-sm rounded-xl border bg-white dark:bg-dark-elevated px-3 outline-none text-light-text dark:text-dark-text">
              <option value="">Select Class</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={batchId} onChange={e => setBatchId(e.target.value)} className="h-11 text-sm rounded-xl border bg-white dark:bg-dark-elevated px-3 outline-none text-light-text dark:text-dark-text">
              <option value="">Select Batch</option>
              <option value="total">Total (All Batches)</option>
              {batches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { if (!instId) { toast.error('Select institute'); return; } setStep('add'); }} disabled={!instId} className="h-11 px-6 rounded-xl bg-lime-500 text-white font-medium hover:bg-lime-600 disabled:opacity-50 flex items-center gap-2">
              Start Adding Students <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          {batchId === 'total' && <p className="text-xs text-light-text-muted">Total mode: loads students from all batches. New students need batch set in CSV editor.</p>}
        </motion.div>
      )}

      {/* ==================== ADD STUDENTS ==================== */}
      {step === 'add' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {students.filter(s => s.dbStatus === 'new').map((student, idx) => (
            <div key={student.id} className="glass-card p-4 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-xs">{idx + 1}</span>
                  <span className="text-sm font-medium text-light-text dark:text-dark-text">New Student #{idx + 1}</span>
                  {student.status === 'processing' && <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-500" />}
                  {student.status === 'done' && <CheckCircle className="w-3.5 h-3.5 text-lime-500" />}
                  {student.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-rose-500" />}
                </div>
                <div className="flex items-center gap-1">
                  {student.status === 'done' && student.extracted?.studentName && <span className="text-xs text-lime-600 truncate max-w-[200px]">{student.extracted.studentName}</span>}
                  {student.status === 'images' && student.images.length > 0 && <button onClick={() => processStudent(student.id)} className="text-xs text-purple-600 flex items-center gap-1 font-medium"><Play className="w-3 h-3" />Process</button>}
                  {student.status === 'error' && <button onClick={() => processStudent(student.id)} className="text-xs text-rose-500 flex items-center gap-1"><RefreshCw className="w-3 h-3" />Retry</button>}
                  {student.logs.length > 0 && <button onClick={() => { const n = new Set(showLogs); n.has(student.id) ? n.delete(student.id) : n.add(student.id); setShowLogs(n); }} className="p-1 text-light-text-muted"><Terminal className="w-3.5 h-3.5" /></button>}
                  <button onClick={() => setDeleteConfirm({ sid: student.id, name: student.extracted?.studentName || `#${idx + 1}` })} className="p-1 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>

              {showLogs.has(student.id) && student.logs.length > 0 && (
                <div className="bg-gray-900 rounded-lg p-2 max-h-32 overflow-y-auto font-mono">
                  {student.logs.map((l, i) => <div key={i} className="text-[10px] text-lime-400">{l}</div>)}
                </div>
              )}

              {student.images.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {student.images.map(img => (
                    <div key={img.id} className="relative group">
                      <div onClick={() => img.preview && setPreviewImg(img.preview)} className="w-16 h-16 rounded-lg overflow-hidden border cursor-pointer hover:border-purple-400">
                        {img.preview ? <img src={img.preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Image className="w-5 h-5 text-light-text-muted" /></div>}
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setStudents(prev => prev.map(s => { if (s.id !== student.id) return s; const img2 = s.images.find(i => i.id === img.id); if (img2?.preview) URL.revokeObjectURL(img2.preview); return { ...s, images: s.images.filter(i => i.id !== img.id) }; })); }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 z-10"><X className="w-3 h-3" /></button>
                      {img.status === 'ocr' && <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg"><Loader2 className="w-4 h-4 animate-spin text-white" /></div>}
                      {img.status === 'done' && <div className="absolute -top-1 -left-1"><CheckCircle className="w-4 h-4 text-lime-500 bg-white rounded-full" /></div>}
                      {img.status === 'error' && <div className="absolute -top-1 -left-1"><AlertCircle className="w-4 h-4 text-rose-500 bg-white rounded-full" /></div>}
                    </div>
                  ))}
                </div>
              )}

              {student.status === 'images' && (
                <div className="flex gap-2">
                  <input type="file" accept="image/*" multiple className="hidden" id={`g-${student.id}`} onChange={e => { if (e.target.files) { addImages(e.target.files); e.target.value = ''; } }} />
                  <label htmlFor={`g-${student.id}`} className="h-9 px-3 rounded-lg border text-xs font-medium hover:bg-light-elevated flex items-center gap-1.5 cursor-pointer text-light-text dark:text-dark-text"><Image className="w-3.5 h-3.5" />Gallery</label>
                  <input type="file" accept="image/*" capture="environment" className="hidden" id={`c-${student.id}`} onChange={e => { if (e.target.files) { addImages(e.target.files); e.target.value = ''; if (continuousCamera) setTimeout(() => document.getElementById(`c-${student.id}`)?.click(), 300); } }} />
                  <label htmlFor={`c-${student.id}`} className="h-9 px-3 rounded-lg border text-xs font-medium hover:bg-light-elevated flex items-center gap-1.5 cursor-pointer text-light-text dark:text-dark-text"><Camera className="w-3.5 h-3.5" />Camera</label>
                </div>
              )}
            </div>
          ))}

          <button onClick={addNewStudent} className="w-full h-12 rounded-2xl border-2 border-dashed font-medium hover:bg-light-elevated flex items-center justify-center gap-2 text-light-text dark:text-dark-text">
            <Plus className="w-5 h-5" /> Add Student
          </button>

          <div className="flex items-center justify-between">
            <button onClick={() => setStep('setup')} className="h-10 px-4 rounded-xl border text-sm font-medium hover:bg-light-elevated flex items-center gap-2 text-light-text dark:text-dark-text"><ArrowLeft className="w-4 h-4" />Back</button>
            <div className="flex gap-2">
              {students.some(s => s.dbStatus === 'new' && s.status === 'images' && s.images.length > 0) && (
                <button onClick={async () => { const toProcess = students.filter(s => s.dbStatus === 'new' && s.status === 'images' && s.images.length > 0); for (const s of toProcess) { await processStudent(s.id); } }} className="h-10 px-4 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 flex items-center gap-2">
                  <Play className="w-4 h-4" />Process All ({students.filter(s => s.dbStatus === 'new' && s.status === 'images' && s.images.length > 0).length})
                </button>
              )}
              <button onClick={exportCSV} disabled={!students.length} className="h-10 px-4 rounded-xl border text-sm font-medium hover:bg-light-elevated flex items-center gap-2 disabled:opacity-50 text-light-text dark:text-dark-text"><Download className="w-4 h-4" />CSV</button>
              <button onClick={async () => { const last = students[students.length - 1]; if (last && last.status === 'images' && last.images.length > 0) processStudent(last.id); await loadExisting(); setStep('review'); }} disabled={loading} className="h-10 px-4 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 flex items-center gap-2 disabled:opacity-50">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}Review & Edit<ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* ==================== CSV EDITOR ==================== */}
      {step === 'review' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <button onClick={() => setStep('add')} className="h-8 px-2 rounded-lg border text-xs hover:bg-light-elevated flex items-center gap-1 text-light-text dark:text-dark-text"><ArrowLeft className="w-3.5 h-3.5" />Back</button>
              <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Review ({students.length})</h2>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => { setStudents(prev => [...prev, { id: uid(), images: [], extracted: { status: 'active', admissionDate: new Date().toISOString().split('T')[0] }, status: 'done', logs: [], dbStatus: 'new', dirty: true }]); }} className="h-9 px-3 rounded-lg bg-lime-500 text-white text-xs font-medium hover:bg-lime-600 flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" />Add Student
              </button>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-light-text-muted" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="h-9 text-xs rounded-lg border pl-7 pr-2 outline-none w-36 text-light-text dark:text-dark-text bg-white dark:bg-dark-elevated" />
              </div>
              <button onClick={exportCSV} className="h-9 px-3 rounded-lg border text-xs font-medium hover:bg-light-elevated flex items-center gap-1.5 text-light-text dark:text-dark-text"><Download className="w-3.5 h-3.5" />CSV</button>
              <button onClick={handleSave} disabled={saving || (!newCount && !dirtyCount)} className="h-9 px-3 rounded-lg border border-purple-200 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 text-xs font-medium disabled:opacity-50 flex items-center gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save ({newCount + dirtyCount})
              </button>
              <button onClick={handlePush} disabled={pushing || !pendingCount} className="h-9 px-3 rounded-lg bg-lime-500 text-white text-xs font-medium hover:bg-lime-600 disabled:opacity-50 flex items-center gap-1.5">
                {pushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}Push ({pendingCount})
              </button>
              <button onClick={loadExisting} disabled={loading} className="h-9 px-3 rounded-lg border text-xs font-medium hover:bg-light-elevated flex items-center gap-1.5 text-light-text dark:text-dark-text">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Reload
              </button>
            </div>
          </div>

          {/* Table */}
          <div ref={scrollRef} onMouseMove={handleScrollMouseMove} onMouseLeave={handleScrollMouseLeave} className="glass-card rounded-2xl overflow-x-auto relative">
            {/* Scroll edge indicators */}
            <div className="pointer-events-none sticky left-0 top-0 bottom-0 w-8 z-20 bg-gradient-to-r from-light-surface/80 dark:from-dark-surface/80 to-transparent opacity-0 hover:opacity-100 transition-opacity" />
            <table className="w-full text-xs">
              <thead className="bg-light-elevated dark:bg-dark-elevated text-light-text-muted sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 w-6">#</th>
                  <th className="px-2 py-2 text-left font-medium">State</th>
                  {ALL_FIELDS.map(([f, label]) => <th key={f} className="px-2 py-2 text-left font-medium whitespace-nowrap">{label}</th>)}
                  <th className="px-2 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border dark:divide-dark-border">
                {filtered.map((student, idx) => {
                  const d = student.extracted;
                  const isExpanded = expanded.has(student.id);
                  return (
                    <>
                    <tr key={student.id} className={`hover:bg-light-elevated/50 dark:hover:bg-dark-elevated/50 cursor-pointer ${student.dbStatus === 'new' ? 'bg-lime-50/30 dark:bg-lime-500/5' : student.dirty ? 'bg-purple-50/30 dark:bg-purple-500/5' : ''}`} onClick={() => { const n = new Set(expanded); n.has(student.id) ? n.delete(student.id) : n.add(student.id); setExpanded(n); }}>
                      <td className="px-2 py-1.5 text-light-text-muted">{idx + 1}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          {isExpanded ? <ChevronUp className="w-3 h-3 text-light-text-muted" /> : <ChevronDown className="w-3 h-3 text-light-text-muted" />}
                          {student.dbStatus === 'new' ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300 font-medium">NEW</span>
                            : student.dbStatus === 'pending' ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-medium">PENDING</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 font-medium">ACTIVE</span>}
                          {student.dirty && <span className="ml-1 text-[10px] text-purple-500">*</span>}
                        </div>
                      </td>
                      {ALL_FIELDS.map(([field, , type]) => (
                        <td key={field} className="px-2 py-1.5" onClick={(e) => { e.stopPropagation(); setActiveCell({ sid: student.id, field }); }}>
                          {type.startsWith('select:') ? (
                            <select value={d[field] || ''} onChange={e => { e.stopPropagation(); updateField(student.id, field, e.target.value); }}
                              className={`bg-transparent outline-none w-full min-w-[60px] ${activeCell?.sid === student.id && activeCell?.field === field ? 'ring-2 ring-purple-500 rounded px-1' : ''}`}>
                              <option value="">-</option>
                              {type.replace('select:', '').split(',').map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          ) : type === 'textarea' ? (
                            <textarea value={d[field] || ''} onChange={e => { e.stopPropagation(); updateField(student.id, field, e.target.value); }} rows={1} onClick={e => e.stopPropagation()}
                              className={`w-full min-w-[120px] bg-transparent outline-none resize-none ${activeCell?.sid === student.id && activeCell?.field === field ? 'ring-2 ring-purple-500 rounded px-1' : ''}`} />
                          ) : (
                            <input type={type} value={d[field] || ''} onChange={e => { e.stopPropagation(); updateField(student.id, field, e.target.value); }} onClick={e => e.stopPropagation()}
                              className={`w-full min-w-[80px] bg-transparent outline-none ${activeCell?.sid === student.id && activeCell?.field === field ? 'ring-2 ring-purple-500 rounded px-1' : ''}`} />
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1">
                          <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ sid: student.id, name: d.studentName || `#${idx + 1}` }); }} className="p-1 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded form view */}
                    {isExpanded && (
                      <tr key={`${student.id}-form`}>
                        <td colSpan={ALL_FIELDS.length + 3} className="p-4 bg-light-surface dark:bg-dark-surface">
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                            {ALL_FIELDS.map(([field, label, type]) => (
                              <div key={field} className={type === 'textarea' ? 'col-span-2' : ''}>
                                <label className="text-[10px] text-light-text-muted dark:text-dark-text-muted font-medium block mb-1">{label}</label>
                                {type.startsWith('select:') ? (
                                  <select value={d[field] || ''} onChange={e => updateField(student.id, field, e.target.value)}
                                    className="w-full h-9 text-sm rounded-lg border bg-white dark:bg-dark-elevated px-2 outline-none focus:ring-2 focus:ring-purple-500 text-light-text dark:text-dark-text">
                                    <option value="">-</option>
                                    {type.replace('select:', '').split(',').map(v => <option key={v} value={v}>{v}</option>)}
                                  </select>
                                ) : type === 'textarea' ? (
                                  <textarea value={d[field] || ''} onChange={e => updateField(student.id, field, e.target.value)} rows={2}
                                    className="w-full text-sm rounded-lg border bg-white dark:bg-dark-elevated px-2 py-1.5 outline-none focus:ring-2 focus:ring-purple-500 text-light-text dark:text-dark-text resize-none" />
                                ) : (
                                  <input type={type} value={d[field] || ''} onChange={e => updateField(student.id, field, e.target.value)}
                                    className="w-full h-9 text-sm rounded-lg border bg-white dark:bg-dark-elevated px-2 outline-none focus:ring-2 focus:ring-purple-500 text-light-text dark:text-dark-text" />
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-light-text-muted text-sm mb-3">No students loaded yet.</p>
                <button onClick={() => { setStudents(prev => [...prev, { id: uid(), images: [], extracted: { status: 'active', admissionDate: new Date().toISOString().split('T')[0] }, status: 'done', logs: [], dbStatus: 'new', dirty: true }]); }} className="h-10 px-4 rounded-xl bg-lime-500 text-white text-sm font-medium hover:bg-lime-600 inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" />Add First Student
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="glass-card rounded-2xl w-full max-w-sm p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-500" /></div>
                <div>
                  <h3 className="font-bold text-light-text dark:text-dark-text">Delete Student?</h3>
                  <p className="text-xs text-light-text-muted">{deleteConfirm.name}</p>
                </div>
              </div>
              <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">This will remove the student and all their images. Cannot be undone.</p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setDeleteConfirm(null)} className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-light-elevated text-light-text dark:text-dark-text">Cancel</button>
                <button onClick={() => doRemove(deleteConfirm.sid)} className="h-9 px-4 rounded-lg bg-rose-500 text-white text-sm font-medium hover:bg-rose-600">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Preview */}
      <AnimatePresence>
        {previewImg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreviewImg(null)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="relative max-w-3xl max-h-[90vh]">
              <img src={previewImg} alt="" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
              <button onClick={() => setPreviewImg(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
